// shortcuts module
// see http://wiki.openstreetmap.org/wiki/Overpass_turbo/Extended_Overpass_Queries
import clamp from "lodash/clamp";

import type {GeocodingResult} from "./geocoding";
import ide from "./ide";
import * as nominatim from "./nominatim";
import type {QueryLang} from "./overpass";

// clamps a coordinate to the given range and rounds it to OSM's precision
// of 7 decimal places (~1cm), avoiding long floating point artifacts
function coord(value: number | string, limit: number) {
  return clamp(Number(value), -limit, limit).toFixed(7);
}

// returns the current visible bbox as a bbox-query
function map2bbox(lang: QueryLang) {
  const bbox =
    ide.map.bboxfilter && ide.map.bboxfilter.isEnabled()
      ? ide.map.bboxfilter.getBounds()
      : ide.map.getBounds();
  const lat1 = coord(bbox.getSouthWest().lat, 90);
  const lat2 = coord(bbox.getNorthEast().lat, 90);
  const lng1 = coord(bbox.getSouthWest().lng, 180);
  const lng2 = coord(bbox.getNorthEast().lng, 180);
  if (lang == "OverpassQL") return `${lat1},${lng1},${lat2},${lng2}`;
  else if (lang == "xml")
    return `s="${lat1}" w="${lng1}" n="${lat2}" e="${lng2}"`;
  else if (lang == "SQL")
    return `st_setsrid(st_makebox2d(st_makepoint(${lng1},${lat1}), st_makepoint(${lng2},${lat2})), 4326)`;
}

// returns the current visible bbox in west,south,east,north order
function map2wsen() {
  const bbox =
    ide.map.bboxfilter && ide.map.bboxfilter.isEnabled()
      ? ide.map.bboxfilter.getBounds()
      : ide.map.getBounds();
  const south = coord(bbox.getSouthWest().lat, 90);
  const north = coord(bbox.getNorthEast().lat, 90);
  const west = coord(bbox.getSouthWest().lng, 180);
  const east = coord(bbox.getNorthEast().lng, 180);
  return `${west},${south},${east},${north}`;
}

// returns the current visible map center as a coord-query
function map2coord(lang: QueryLang) {
  const center = ide.map.getCenter();
  const lat = coord(center.lat, 90);
  const lng = coord(center.lng, 180);
  if (lang == "OverpassQL") return `${lat},${lng}`;
  else if (lang == "xml") return `lat="${lat}" lon="${lng}"`;
}

// converts relative time to ISO time string
async function relativeTime(instr: string): Promise<string> {
  const now = Date.now();
  // very basic differential date
  if (instr == "") instr = "0 seconds";
  const match = instr
    .toLowerCase()
    .match(
      /(-?[0-9]+) ?(seconds?|minutes?|hours?|days?|weeks?|months?|years?)?/
    );
  if (match === null) return ""; // todo: throw an error. do not silently fail
  const count = parseInt(match[1]);
  let interval: number;
  switch (match[2]) {
    case "second":
    case "seconds":
      interval = 1;
      break;
    case "minute":
    case "minutes":
      interval = 60;
      break;
    case "hour":
    case "hours":
      interval = 3600;
      break;
    case "day":
    case "days":
    default:
      interval = 86400;
      break;
    case "week":
    case "weeks":
      interval = 604800;
      break;
    case "month":
    case "months":
      interval = 2628000;
      break;
    case "year":
    case "years":
      interval = 31536000;
      break;
  }
  const date = now - count * interval * 1000;
  return new Date(date).toISOString();
}

// geocoded values (object/area ids, coords, bbox)
async function geocodeId(instr: string): Promise<string> {
  const lang = ide.getQueryLang();
  function filter(n: GeocodingResult) {
    return n.osm_type && n.osm_id;
  }
  let res: GeocodingResult;
  try {
    res = await nominatim.getBest(instr, filter);
  } catch (error) {
    ide.onNominatimError(instr, "Id");
    throw error;
  }
  if (lang == "OverpassQL") return `${res.osm_type}(${res.osm_id})`;
  if (lang == "xml") return `type="${res.osm_type}" ref="${res.osm_id}"`;
  // todo: there is no SQL representation for this shortcut
  return String(res);
}
async function geocodeArea(instr: string): Promise<string> {
  const lang = ide.getQueryLang();
  function filter(n: GeocodingResult) {
    return n.osm_type && n.osm_id && n.osm_type !== "node";
  }
  let res: GeocodingResult;
  try {
    res = await nominatim.getBest(instr, filter);
  } catch (error) {
    ide.onNominatimError(instr, "Area");
    throw error;
  }
  let area_ref: number | string = 1 * res.osm_id;
  if (res.osm_type == "way") area_ref += 2400000000;
  if (res.osm_type == "relation") area_ref += 3600000000;
  if (lang == "OverpassQL") {
    // Do not +2400000000 for ways since version 0.7.57,
    // for backward compatibility query both IDs, see
    // https://github.com/tyrasd/overpass-turbo/issues/537
    if (res.osm_type === "way") area_ref = `${area_ref},${res.osm_id}`;
    return `area(id:${area_ref})`;
  }
  if (lang == "xml") {
    // https://github.com/tyrasd/overpass-turbo/issues/537
    if (res.osm_type === "way") area_ref = `${area_ref}" ref_1="${res.osm_id}`;
    return `type="area" ref="${area_ref}"`;
  }
  // todo: there is no SQL representation for this shortcut
  return "";
}
async function geocodeBbox(instr: string): Promise<string> {
  const lang = ide.getQueryLang();
  let res: GeocodingResult;
  try {
    res = await nominatim.getBest(instr);
  } catch (error) {
    ide.onNominatimError(instr, "Bbox");
    throw error;
  }
  const lat1 = coord(res.bounds[0], 90);
  const lng1 = coord(res.bounds[1], 180);
  const lat2 = coord(res.bounds[2], 90);
  const lng2 = coord(res.bounds[3], 180);
  if (lang == "OverpassQL") return `${lat1},${lng1},${lat2},${lng2}`;
  if (lang == "xml") return `s="${lat1}" w="${lng1}" n="${lat2}" e="${lng2}"`;
  // todo: there is no SQL representation for this shortcut
  return String(res);
}
async function geocodeCoords(instr: string): Promise<string> {
  const lang = ide.getQueryLang();
  let res: GeocodingResult;
  try {
    res = await nominatim.getBest(instr);
  } catch (error) {
    ide.onNominatimError(instr, "Coords");
    throw error;
  }
  if (lang == "OverpassQL") return `${res.lat},${res.lon}`;
  if (lang == "xml") return `lat="${res.lat}" lon="${res.lon}"`;
  // todo: there is no SQL representation for this shortcut
  return String(res);
}

export type Shortcut = string | ((instr: string) => Promise<string>);

export default function shortcuts(): Record<string, Shortcut> {
  const queryLang = ide.getQueryLang();
  return {
    bbox: map2bbox(queryLang),
    wsen: map2wsen(),
    center: map2coord(queryLang),
    // special handling for global bbox in xml queries (which uses an OverpassQL-like notation instead of n/s/e/w parameters):
    __bbox__global_bbox_xml__ezs4K8__: map2bbox("OverpassQL"),
    date: relativeTime,
    geocodeId: geocodeId,
    geocodeArea: geocodeArea,
    geocodeBbox: geocodeBbox,
    geocodeCoords: geocodeCoords,
    // legacy
    nominatimId:
      queryLang == "xml"
        ? geocodeId
        : async (instr) => `${await geocodeId(instr)};`,
    nominatimArea:
      queryLang == "xml"
        ? geocodeArea
        : async (instr) => `${await geocodeArea(instr)};`,
    nominatimBbox: geocodeBbox,
    nominatimCoords: geocodeCoords
  };
}
